/* eslint-disable space-before-function-paren */
import { post, patch, get, put } from '../../../api';
import { buildUrl } from '../../request.utils';

/**
 * Send user invitations via POST /invitations.
 *
 * @param {Object} params
 * @param {string[]} params.recipients      - List of email addresses to invite
 * @param {string}   params.assigned_role   - Role to assign (e.g. 'traffic_viewer')
 * @param {string}   params.agency_id       - Agency the invitees will belong to
 * @param {string[]} [params.jurisdictions] - Optional jurisdiction IDs (for traffic_viewer)
 * @param {string}   [params.customized_message] - Optional personal message
 * @returns {Promise<Object>} - API response data
 */
export const sendInvitations = async ({
    recipients,
    assigned_role,
    agency_id,
    jurisdictions,
    customized_message,
} = {}) => {
    const url = '/invitations';

    const body = {
        recipients,
        assigned_role,
        agency_id,
    };

    // Only include optional fields if they have values
    if (jurisdictions?.length) {
        body.jurisdictions = jurisdictions;
    }
    if (customized_message) {
        body.customized_message = customized_message;
    }

    try {
        const { data } = await post(url, body);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Send LYT internal team invitations via POST /invitations/lyt.
 *
 * Unlike sendInvitations, this does NOT include agency_id in the body
 * because LYT internal users are not associated with a customer agency.
 *
 * @param {Object} params
 * @param {string[]} params.recipients      - List of email addresses to invite
 * @param {string}   params.assigned_role   - Role to assign (e.g. 'lyt_admin')
 * @param {string}   [params.customized_message] - Optional personal message
 * @returns {Promise<Object>} - API response data
 */
export const sendLytInvitations = async ({
    recipients,
    assigned_role,
    customized_message,
} = {}) => {
    const url = '/invitations/lyt';

    const body = {
        recipients,
        assigned_role,
    };

    if (customized_message) {
        body.customized_message = customized_message;
    }

    try {
        const { data } = await post(url, body);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Update an invitation via PATCH /invitations/update.
 *
 * Used for multiple purposes including role updates (request_type: 'update_role')
 * and resending expired invitations (request_type: 'retry_invite').
 * The invitation is identified by three query params: agency_id, invitee_email, created_at_ts.
 *
 * @param {Object} params
 * @param {string}   params.agency_id       - Agency ID the invitation belongs to
 * @param {string}   params.invitee_email   - Email of the invitee
 * @param {string}   params.created_at_ts   - ISO timestamp when the invitation was created
 * @param {string}   params.assigned_role   - New role to assign (e.g. 'traffic_viewer')
 * @param {string[]} [params.jurisdictions] - Jurisdiction IDs (for traffic_viewer)
 * @param {string}   params.request_type    - Type of request (e.g. 'update_role')
 * @param {string}   [params.change_reason] - Reason for the change
 * @returns {Promise<Object>} - API response data
 */
export const updateInvitation = async ({
    agency_id,
    invitee_email,
    created_at_ts,
    assigned_role,
    jurisdictions,
    request_type,
    change_reason,
} = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('agency_id', agency_id);
    queryParams.append('invitee_email', invitee_email);
    queryParams.append('created_at_ts', created_at_ts);
    const url = `/invitations/update?${queryParams.toString()}`;

    const body = {
        assigned_role,
        request_type,
    };

    // Only include optional fields if they have values
    if (jurisdictions?.length) {
        body.jurisdictions = jurisdictions;
    }
    if (change_reason) {
        body.change_reason = change_reason;
    }

    try {
        const { data } = await patch(url, body);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Approve or reject an invitation via PATCH /invitations/approve.
 *
 * The invitation is identified by three query params: agency_id, invitee_email, created_at_ts.
 *
 * @param {Object} params
 * @param {string}  params.agency_id       - Agency ID the invitation belongs to
 * @param {string}  params.invitee_email   - Email of the invitee
 * @param {string}  params.created_at_ts   - ISO timestamp when the invitation was created
 * @param {boolean} params.is_approved     - true to approve, false to reject
 * @param {string}  [params.change_reason] - Reason for the decision
 * @returns {Promise<Object>} - API response data
 */
export const approveRejectInvitation = async ({
    agency_id,
    invitee_email,
    created_at_ts,
    is_approved,
    change_reason,
} = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('agency_id', agency_id);
    queryParams.append('invitee_email', invitee_email);
    queryParams.append('created_at_ts', created_at_ts);
    const url = `/invitations/approve?${queryParams.toString()}`;

    const body = {
        is_approved,
    };

    if (change_reason) {
        body.change_reason = change_reason;
    }

    try {
        const { data } = await patch(url, body);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Validate an invitation token before account setup.
 * Replaces legacy `GET /users/:userID/invite/:inviteToken`.
 */
export const getInvitationByToken = async ({ inviteToken, token } = {}) => {
    const invitationToken = inviteToken ?? token;
    const url = buildUrl('/invitations/accept', { token: invitationToken });

    try {
        const { data } = await get(url);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Complete invitation registration (account setup).
 * Replaces legacy `PUT /users/:userID/invite/:inviteToken`.
 */
export const completeInvitationRegistration = async (reqData = {}, { inviteToken, token } = {}) => {
    const invitationToken = inviteToken ?? token;
    const url = buildUrl('/invitations/accept', { token: invitationToken });

    try {
        const response = await put(url, reqData);
        return response;
    } catch (err) {
        throw err.response?.data || err;
    }
};
